import {Box, Icon, Show, Text, VStack} from 'platform/foundation';
import styled from 'styled-components';

import {useId} from 'react';

import {isNotNil} from 'ramda';

import type {TestIdProps} from 'shared/web';

import type {AiComposerSuggestion} from '../AiComposerDraft';
import {withAlphaHex} from '../withAlphaHex';
import {AiComposerMark} from './AiComposerMark';

interface AiComposerSuggestionButtonProps extends TestIdProps {
  suggestion: AiComposerSuggestion;
  isDisabled: boolean;
  onSelect: (instruction: string) => void;
}

// Hex alpha suffixes: 0.95 of white for the raised fill, 0.28 of the accent for its border.
const ALPHA_FILL = 'F2';
const ALPHA_BORDER = '47';
const HAIRLINE = '1px solid';

export function AiComposerSuggestionButton(props: AiComposerSuggestionButtonProps) {
  const titleId = useId();
  const descriptionId = useId();

  return (
    <Option
      type="button"
      disabled={props.isDisabled}
      aria-labelledby={titleId}
      aria-describedby={isNotNil(props.suggestion.description) ? descriptionId : undefined}
      onClick={() => props.onSelect(props.suggestion.instruction)}
      data-testid={props['data-testid']}
    >
      <AiComposerMark size={7} />
      <Box flex={1} minWidth={0}>
        <VStack spacing={0}>
          <span id={titleId}>
            <Text size="small" overflowWrap="anywhere">
              {props.suggestion.title}
            </Text>
          </span>
          <Show when={isNotNil(props.suggestion.description)}>
            <span id={descriptionId}>
              <Text size="xSmall" color="tertiary" overflowWrap="anywhere">
                {props.suggestion.description}
              </Text>
            </span>
          </Show>
        </VStack>
      </Box>
      <Go aria-hidden>
        <Icon value="navigation/arrow_forward" size={4} color="palettes.neutral.60.100" />
      </Go>
    </Option>
  );
}

const Go = styled.span`
  display: inline-flex;
  align-items: center;
  flex: none;
  opacity: 0;
  transition: opacity 120ms ease;
`;

const Option = styled.button`
  display: flex;
  align-items: flex-start;
  gap: ${({theme}) => theme.getSize(2)};
  width: 100%;
  min-width: 0;
  padding: ${({theme}) => theme.getSize(2)};
  border: ${HAIRLINE} transparent;
  border-radius: ${({theme}) => theme.radii.medium};
  background: transparent;
  text-align: left;
  cursor: pointer;
  user-select: none;
  transition:
    background 120ms ease,
    border-color 120ms ease,
    box-shadow 120ms ease;

  &:hover:enabled,
  &:focus-visible {
    background: ${({theme}) => withAlphaHex(theme.colors.palettes.white[10][100], ALPHA_FILL)};
    border-color: ${({theme}) => withAlphaHex(theme.colors.palettes.purple[60][100], ALPHA_BORDER)};
    box-shadow: ${({theme}) => theme.shadows.elevation_1};
  }

  &:hover:enabled ${Go}, &:focus-visible ${Go} {
    opacity: 1;
  }

  &:focus-visible {
    outline: ${({theme}) => theme.outline.default}
      ${({theme}) => theme.colors.palettes.purple[60][100]};
    outline-offset: ${({theme}) => theme.outline.offset};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;
